"use client"

import { MapPin } from "lucide-react"
import { useEffect, useState } from "react"
import { Outlet, useOutletContext } from "react-router-dom"
import { Spinner } from "@web/components/ui/spinner"
import { env } from "@web/lib/env"
import { getCoordinates } from "@web/lib/utils"
import { ErrorDisplay } from "./error-display"
import { useLanguage } from "@web/lib/contexts/language-context"

interface GeolocationState {
  coords: GeolocationPosition["coords"] | undefined
  isGeolocationAvailable: boolean
  isGeolocationEnabled: boolean
  isLoading: boolean
  error: string | null
  isGeoError: boolean
}

const enableHighAccuracy = true
const timeout = 60000
const maximumAge = 0
const watchPosition = false

function LocationLoadingState() {
  const { t } = useLanguage()

  return (
    <div className="flex flex-1 flex-col items-center justify-center min-h-[60vh] px-4">
      <div className="flex flex-col items-center gap-6 text-center max-w-md">
        <div className="relative">
          <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse" />
          <div className="relative flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full">
            <MapPin className="w-10 h-10 text-primary" />
          </div>
        </div>

        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-foreground">{t.geoProtectRoute.title}</h2>
          <p className="text-muted-foreground text-sm">
            {t.geoProtectRoute.description}
          </p>
        </div>

        <div className="flex items-center gap-2 text-muted-foreground">
          <Spinner className="w-5 h-5" />
          <span className="text-sm">{t.geoProtectRoute.loading}</span>
        </div>
      </div>
    </div>
  )
}

export function GeoProtectedRoute() {
  const isBrowser = typeof window !== "undefined" && typeof navigator !== "undefined"
  const isGeolocationAvailable = isBrowser && "geolocation" in navigator

  const [state, setState] = useState<GeolocationState>({
    coords: undefined,
    isGeolocationAvailable,
    isGeolocationEnabled: false,
    isLoading: true,
    error: null,
    isGeoError: false,
  })

  useEffect(() => {
    if (!state.isGeolocationAvailable) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Geolocalização não disponível. Este dispositivo não suporta geolocalização.",
        isGeoError: true,
      }))
      return
    }

    const handleSuccess = (position: GeolocationPosition) => {
      setState((prev) => ({
        ...prev,
        coords: position.coords,
        isGeolocationEnabled: true,
        isLoading: false,
        error: null,
        isGeoError: false,
      }))
    }

    const handleError = (error: GeolocationPositionError) => {
      console.log("error: ", error)
      let errorMessage = ""
      switch (error.code) {
        case 1:
          errorMessage = "Geolocalização desabilitada. Habilite a geolocalização nas configurações do navegador."
          break
        case 2:
          errorMessage = "Localização indisponível. A geolocalização é obrigatória para verificação de selos."
          break
        case 3:
          errorMessage = "Tempo limite excedido. Não foi possível obter sua localização."
          break
        default:
          errorMessage = "Erro ao obter localização. A geolocalização é obrigatória para verificação de selos."
      }

      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        isGeoError: true,
        isGeolocationEnabled: false,
      }))
    }

    const positionOptions: PositionOptions = {
      enableHighAccuracy,
      timeout,
      maximumAge,
    }

    if (watchPosition) {
      const watchId = navigator.geolocation.watchPosition(handleSuccess, handleError, positionOptions)
      return () => navigator.geolocation.clearWatch(watchId)
    }

    navigator.geolocation.getCurrentPosition(handleSuccess, handleError, positionOptions)
  }, [state.isGeolocationAvailable])

  const getCurrentCoordinates = () => {
    const currentCoords = getCoordinates(state.coords)
    if (!currentCoords) {
      const isDev = env.MODE === "development"
      if (isDev && !env.VITE_PUBLIC_DEV_LATITUDE) {
        return {
          error:
            "Coordenadas não disponíveis. Configure VITE_PUBLIC_DEV_LATITUDE e VITE_PUBLIC_DEV_LONGITUDE no .env.local",
          coords: null,
        }
      }
    }
    return { coords: currentCoords, error: null }
  }

  if (state.isLoading) return <LocationLoadingState />

  if (state.isGeoError)
    return (
      <ErrorDisplay
        message={state.error || "Erro ao obter localização."}
        onRetry={() => window.location.reload()}
        isGeolocationError
      />
    )

  return <Outlet context={{ ...state, getCurrentCoordinates }} />
}

export type UseLocationProps = GeolocationState & {
  getCurrentCoordinates: () => { coords: GeolocationPosition | null; error: string | null }
}

export const useGeolocation = () => {
  const context = useOutletContext<UseLocationProps>()
  if (!context) {
    return {
      coords: null,
      isGeolocationAvailable: false,
      isGeolocationEnabled: false,
      isLoading: false,
      error: "Contexto de geolocalização não disponível",
      isGeoError: true,
      getCurrentCoordinates: () => ({ coords: null, error: "Contexto de geolocalização não disponível" }),
    }
  }
  return context
}
